import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field } from "formik";
import { get, isEmpty } from "lodash";
import moment from "moment";
import React from "react";
import {
  DatePicker,
  FieldButtonGroup,
  FieldDropSide,
  FieldInputDollar,
  FieldInputNumber,
  SearchableList,
} from "../../../components";
import {
  clearFieldValues,
  OPTIONS_DATA_MAPPER,
  returnOnlyIfBoolean,
  toOptionValuesFromMapper,
  usDateFormat,
  yesNoOptions,
} from "../../../utils";
import {
  calculateAgeFromDOB,
  calculateNextPaymentDate,
  DateOfAnnuityPayments,
  DateOfAnnuityPaymentsEnum,
  TypeOfExpectancy,
} from "./DistributionConstantsAndFunctions";
const HeadingAndTextComponent = ({
  headingText = "",
  contentText = "",
  style = {},
}) => (
  <div style={{ marginTop: "8px", ...style }}>
    <div className="body_color">{headingText}</div>
    <div className="textWord mt-2">{contentText}</div>
  </div>
);

function SpecificTenureDetails(props) {
  const {
    fields,
    withdrawalRequestId,
    values,
    className = "",
    style = {},
    vestBalance,
    employeeDetails,
    setFieldValue,
    setValues,
    handleChange,
    periodicPaymentCalculationDetails,
    selectedWithdrawalPlanSetUp,
    withdrawalAmountForSpecificTenure,
    specificTenureData,
    ...rest
  } = props;
  const paymentFrequencyOptions = toOptionValuesFromMapper(
    DateOfAnnuityPayments
  ).filter((_) =>
    selectedWithdrawalPlanSetUp?.dateOfAnnuityPayments?.includes(_.value)
  );

  const isEmployeeMarried = employeeDetails.maritalStatus == "M";
  const specificTenureOptions = toOptionValuesFromMapper(
    TypeOfExpectancy
  ).filter((_) => isEmployeeMarried || _.value !== 1);
  const disbursementFrequencyOptions = toOptionValuesFromMapper(
    OPTIONS_DATA_MAPPER.PAYROLLFREQUENCY_TYPE
  ).filter((_) => [4, 5, 6, 7].includes(_.value));

  const nextRepaymentDateDefault = calculateNextPaymentDate(
    selectedWithdrawalPlanSetUp
  );
  return (
    <div style={{ ...style }} className={className}>
      <div className="heading-div">Specific Tenure Details</div>
      <div className="basic-details-card">
        <div className="d-flex justify-content-between w-100">
          <HeadingAndTextComponent
            headingText="Total Available Amount"
            contentText={`$ ${parseInt(
              values[fields.netAmountRequested]
            ).toFixed(2)}`}
            style={{ marginTop: "auto" }}
          />
          {(values[fields.typeOfExpectancy] === 0 ||
            values[fields.typeOfExpectancy] === 1 ||
            (values[fields.typeOfExpectancy] === 2 &&
              values[fields.numberOfInstallments])) &&
            values[fields.frequency] && (
              <HeadingAndTextComponent
                headingText="Withdrawal amount"
                contentText={`$ ${withdrawalAmountForSpecificTenure.toFixed(
                  2
                )} / ${
                  OPTIONS_DATA_MAPPER.PAYROLLFREQUENCY_TYPE[
                    values[fields.frequency]
                  ]
                }`}
                style={{
                  marginTop: "auto",
                  background: "#F6F6F6",
                  padding: "10px",
                }}
              />
            )}
        </div>
        <p className="body_color mt-25">Specific Tenure</p>
        <Field
          size="md"
          name={fields.typeOfExpectancy}
          value={values[fields.typeOfExpectancy]}
          options={specificTenureOptions}
          direction="bottom"
          placeholder="Specific Tenure"
          disabled={!isEmpty(withdrawalRequestId)}
          popupContent={
            <SearchableList
              label="Specific Tenure"
              options={specificTenureOptions}
              selectedValue={values[fields.typeOfExpectancy]}
              onSelect={(value) => {
                let updatedValues = clearFieldValues({
                  values,
                  fieldsToClear: [
                    fields.dateOfAnnuityPayments,
                    fields.frequency,
                    fields.nextRepaymentDate,
                  ],
                });
                updatedValues[fields.typeOfExpectancy] = value;
                setValues(updatedValues);
              }}
            />
          }
          component={FieldDropSide}
        />
        <p className="body_color ft-12">
          <FontAwesomeIcon
            icon={faInfoCircle}
            style={{ fontSize: "16px" }}
            className="mr-2"
            color="#828282"
          />
          Calculate from IRS table
        </p>
        {![null, undefined, ""].includes(values[fields.typeOfExpectancy]) && (
          <>
            {values[fields.typeOfExpectancy] === 2 && (
              <>
                <Field
                  size="smd"
                  placeholder="Enter installment"
                  label="Enter installment"
                  isRequired
                  name={fields.numberOfInstallments}
                  type="text"
                  autoComplete="off"
                  value={values[fields.numberOfInstallments]}
                  onChange={handleChange}
                  component={FieldInputNumber}
                />
                <p className="body_color ft-12">
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    style={{ fontSize: "16px" }}
                    className="mr-2"
                    color="#828282"
                  />
                  Max - 36
                </p>
              </>
            )}

            {values[fields.typeOfExpectancy] !== 2 && (
              <HeadingAndTextComponent
                headingText="Participant Life Expectency"
                contentText={specificTenureData?.lifeExpectancy}
                style={{ marginTop: "auto" }}
              />
            )}

            <HeadingAndTextComponent
              headingText="Participant Date Of Birth"
              contentText={moment(employeeDetails?.birthDate).format(
                "MM/DD/YYYY"
              )}
            />

            <HeadingAndTextComponent
              headingText="Participant Current Age"
              contentText={calculateAgeFromDOB(employeeDetails?.birthDate)}
            />
            {values[fields.typeOfExpectancy] === 1 && (
              <HeadingAndTextComponent
                headingText="Beneficiary Current Age"
                contentText={specificTenureData?.spouseAge}
              />
            )}

            <p className="body_color" style={{ marginTop: "20px" }}>
              Payment Frequency
            </p>
            <Field
              isRequired
              name={fields.dateOfAnnuityPayments}
              size="sm"
              options={paymentFrequencyOptions}
              selectedValue={values[fields.dateOfAnnuityPayments]}
              onChange={(value) => {
                const tempValues = { ...values };
                tempValues[fields.dateOfAnnuityPayments] = value;
                if (value == 1) {
                  tempValues[fields.frequency] =
                    selectedWithdrawalPlanSetUp?.paymentFrequencyApplicable;
                  tempValues[
                    fields.nextRepaymentDate
                  ] = nextRepaymentDateDefault;
                } else {
                  tempValues[fields.frequency] = null;
                  tempValues[fields.nextRepaymentDate] = null;
                }
                setValues(tempValues);
              }}
              component={FieldButtonGroup}
            />
            {values[fields.dateOfAnnuityPayments] ===
              DateOfAnnuityPaymentsEnum.FixedDate && (
              <>
                <HeadingAndTextComponent
                  headingText="Disbursement Frequency"
                  contentText={
                    OPTIONS_DATA_MAPPER.PAYROLLFREQUENCY_TYPE[
                      selectedWithdrawalPlanSetUp?.paymentFrequencyApplicable
                    ]
                  }
                  style={{ marginTop: "auto" }}
                />
                <HeadingAndTextComponent
                  headingText="Date"
                  contentText={nextRepaymentDateDefault}
                />
              </>
            )}
            {values[fields.dateOfAnnuityPayments] ===
              DateOfAnnuityPaymentsEnum.PartipicantSelected && (
              <>
                <Field
                  size="md"
                  name={fields.frequency}
                  isRequired
                  value={values[fields.frequency]}
                  options={disbursementFrequencyOptions}
                  direction="bottom"
                  placeholder="Disbursement frequency"
                  label="Disbursement frequency"
                  disabled={!isEmpty(withdrawalRequestId)}
                  validate={(value) =>
                    ["", undefined, null].includes(value) &&
                    isEmpty(values[fields.nextRepaymentDate])
                      ? "Required"
                      : null
                  }
                  popupContent={
                    <SearchableList
                      label="Disbursement frequency"
                      options={disbursementFrequencyOptions}
                      selectedValue={values[fields.frequency]}
                      onSelect={(value) => {
                        setFieldValue(fields.frequency, value);
                      }}
                    />
                  }
                  component={FieldDropSide}
                />
                <Field
                  size="sm"
                  label="Date"
                  isRequired
                  name={fields.nextRepaymentDate}
                  value={usDateFormat(values[fields.nextRepaymentDate])}
                  direction="bottom"
                  isDatePicker
                  onClear={() => setFieldValue(fields.nextRepaymentDate, "")}
                  validate={(value) =>
                    ["", undefined, null, "Select One"].includes(value) &&
                    isEmpty(values[fields.nextRepaymentDate])
                      ? "Required"
                      : null
                  }
                  popupContent={
                    <DatePicker
                      onDayClick={(value) =>
                        setFieldValue(fields.nextRepaymentDate, value)
                      }
                      value={usDateFormat(values[fields.nextRepaymentDate])}
                    />
                  }
                  component={FieldDropSide}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SpecificTenureDetails;
